import { Button, DayStrip, ReceiptPayment, addDays, firstOfMonth, formatDayShort, formatMonth, lastOfMonth, spanDays, type DayRange } from '@krp/ui-kit'
import { cashierMoney, receiptColor } from '../model/analytics'
import { shiftDays, shiftsOfMonth } from '../model/shifts'
import type { ReceiptShifts } from '../model/types'
import './shifts.css'

export interface CashierShiftsTabProps {
  today: string
  /** Отбор периода: весь месяц или выбранные дни; месяц графика — месяц `range.from`. */
  range: DayRange
  timeZoneId: string
  place: string
  data: ReceiptShifts | undefined
  busy: boolean
  /** Отчёт за месяц ещё читается: смен пока нет не потому, что их не было. */
  pending?: boolean
  onPeriod: (range: DayRange) => void
  /** Стрелка месяца: прочитать весь показанный месяц. */
  onMonth: (month: string) => void
  /** «Продажи смены»: открыть вкладку «Продажи» на днях этой смены. */
  sales: (range: DayRange) => void
}

const shiftsWord = (count: number) => count % 10 === 1 && count % 100 !== 11 ? 'смена' : [2, 3, 4].includes(count % 10) && ![12, 13, 14].includes(count % 100) ? 'смены' : 'смен'

/**
 * Вкладка «Смены» статистики кассира: месяц стрелками (как во вкладке «Чеки»), по дням месяца — в какие дни кассир выходил на
 * смену, ниже — смены по отбору: источник, касса, номер, открытие и закрытие, чеки, приход, возвраты и оплата.
 * День со сменой отличается столбцом и числом смен, день без смены — прочерком: не только цветом.
 */
export function CashierShiftsTab(props: CashierShiftsTabProps) {
  const month = firstOfMonth(props.range.from || props.today)
  const end = lastOfMonth(month)
  const shifts = shiftsOfMonth(props.data, month, props.timeZoneId, props.place)
  const days = shiftDays(shifts, month)
  const top = Math.max(1, ...[...days.values()].map((day) => day.saleCents))
  const whole = props.range.from <= month && props.range.to >= (end > props.today ? props.today : end)
  const shown = whole ? shifts : shifts.filter(({ range }) => range !== null && range.from <= props.range.to && range.to >= props.range.from)
  const at = (value: string | null) => value === null ? null : new Date(value).toLocaleString('ru-RU', { timeZone: props.timeZoneId, day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
  const period = whole ? formatMonth(month) : props.range.from === props.range.to ? formatDayShort(props.range.from) : `${formatDayShort(props.range.from)} — ${formatDayShort(props.range.to)}`
  const loading = props.busy || props.pending === true
  return <div className="cashier-shifts">
    <DayStrip pager today={props.today} value={props.range} onChange={props.onPeriod} onMonth={props.onMonth} label="Месяц смен" />
    <section className="krp-month-trend cashier-shifts__chart" aria-label="Смены по дням месяца" aria-busy={loading}>
      <div className="krp-month-trend__heading">
        <h3 className="krp-trend__title">Выходы на смену · {formatMonth(month)}</h3>
        <span className="krp-month-trend__status" role="status">{loading ? 'Загружаем смены…' : `${days.size} дн. со сменами · ${shifts.length} ${shiftsWord(shifts.length)}`}</span>
      </div>
      <ol className="cashier-shifts__days" style={{ gridTemplateColumns: `repeat(${spanDays(month, end)}, minmax(18px, 1fr))` }}>
        {Array.from({ length: spanDays(month, end) }, (_, index) => {
          const day = addDays(month, index)
          const value = days.get(day)
          const pressed = !whole && day >= props.range.from && day <= props.range.to
          const label = value === undefined ? `${formatDayShort(day)}: смены не было` : `${formatDayShort(day)}: ${value.count} ${shiftsWord(value.count)}, приход ${cashierMoney(value.saleCents / 100)}`
          return <li key={day}>
            <button type="button" className="cashier-shifts__day" data-shift={value === undefined ? undefined : true} aria-pressed={pressed} aria-label={label} title={label}
              disabled={day > props.today} onClick={() => props.onPeriod({ from: day, to: day })}>
              <span className="cashier-shifts__count" aria-hidden="true">{value === undefined ? '—' : value.count}</span>
              <i aria-hidden="true" style={value === undefined ? undefined : { height: `${Math.max(6, Math.round(value.saleCents / top * 90))}px` }} />
              <small aria-hidden="true">{Number(day.slice(8, 10))}</small>
            </button>
          </li>
        })}
      </ol>
    </section>
    <section className="receipts-service__shifts" aria-label="Смены кассира" aria-busy={loading}>
      <div className="cashier-shifts__heading">
        <h3>Смены · {period}{loading ? '' : `: ${shown.length}`}</h3>
        {!whole && <Button actionId="receipts.cashier-analytics.filter" onClick={() => props.onPeriod({ from: month, to: end })}>Весь месяц</Button>}
      </div>
      {props.data !== undefined && !props.data.complete && <p role="status">Данные смен неполные. Отсутствие смены не означает отсутствие продаж.</p>}
      {shown.length > 0 && <div className="krp-receipt-table-wrap"><table className="krp-receipt-table cashier-shifts__table" aria-label="Смены кассира за период">
        <thead><tr><th scope="col">Смена</th><th scope="col">Открыта</th><th scope="col">Закрыта</th><th scope="col">Чеков</th><th scope="col">Приход</th><th scope="col">Возвраты</th><th scope="col">Оплата</th><th scope="col">Действие</th></tr></thead>
        <tbody>{shown.map(({ shift, range }) => {
          const opened = at(shift.openedAt), closed = at(shift.closedAt)
          const first = at(shift.firstReceiptAt), last = at(shift.lastReceiptAt)
          return <tr key={`${shift.resourceId}:${shift.registerKey}:${shift.shiftKey}`}>
            <th scope="row"><strong>№ {shift.number || 'не передан'}</strong>
              <span className="krp-receipt-table__source"><i style={{ backgroundColor: receiptColor(shift.resourceId, shift.registerKey) }} aria-hidden="true" />{shift.resourceName}</span>
              <small>{[shift.shopName.trim(), shift.registerName || 'Касса без названия'].filter((part) => part !== '').join(' · ')}</small>
              {shift.identityStrength !== 'stable-id' && <small role="status">{shift.identityStrength === 'register-number-day' ? 'Сгруппирована по кассе, номеру и дате чеков' : 'Группа чеков: границы смены источник не передал'}</small>}
            </th>
            <td>{opened ?? <>не передано<small>первый чек: {first ?? '—'}</small></>}</td>
            <td>{closed ?? (shift.openedAt !== null ? <>не закрыта или не передано<small>последний чек: {last ?? '—'}</small></> : <>не передано<small>последний чек: {last ?? '—'}</small></>)}</td>
            <td>{shift.receiptCount}</td>
            <td>{shift.amountsComplete ? '' : '≥ '}{cashierMoney(shift.saleAmount)}</td>
            <td>{shift.amountsComplete ? '' : '≥ '}{cashierMoney(shift.refundAmount)}</td>
            <td><ReceiptPayment total={shift.amountsComplete ? shift.saleAmount - shift.refundAmount : null} cash={shift.paymentsComplete ? shift.cashAmount : null} card={shift.paymentsComplete ? shift.cardAmount : null} /></td>
            <td><Button actionId="receipts.cashier-analytics.shift-sales" disabled={range === null} onClick={() => { if (range !== null) props.sales(range) }}>Продажи смены</Button></td>
          </tr>
        })}</tbody>
      </table></div>}
      {!loading && shown.length === 0 && <p role="status">{whole ? 'За этот месяц смен с чеками кассира нет.' : 'В выбранные дни смен нет.'}</p>}
    </section>
  </div>
}
